<template>
  <Teleport to="#teleports">
    <transition name="fade">
      <div
        v-if="shouldShow"
        class="opacity-100 fixed top-0 bottom-0 right-0 left-0 bg-overlay-default z-overlay"
        tabindex="0"
        role="button"
        aria-label="Stäng popup-fönstret"
        @click="close()"
        @keyup.esc.self.prevent="close()"
      ></div>
    </transition>
    <transition name="slide">
      <div
        v-if="shouldShow"
        ref="scrollRef"
        role="dialog"
        :aria-label="dialogTitel"
        :class="[
          'w-full',
          mediumWidthClass,
          'fixed',
          'right-0',
          'top-0',
          'bottom-0',
          'overflow-y-auto',
          'overflow-x-hidden',
          'bg-surface-background',
          'z-modal',
          drawerExtraClasses ? drawerExtraClasses : null,
        ]"
      >
        <div ref="focusTarget">
          <div :class="{ 'pb-180 lg:pb-100': $slots.footer }">
            <IconButton
              aria-label="Stäng popup-fönstret"
              classes="absolute top-[0.8rem] right-[0.4rem]"
              icon-name="x"
              @click="close()"
              @keydown.enter.self.prevent="close()"
              @keydown.space.self.prevent="close()"
              @keydown.esc="close()"
            />
            <div v-if="$slots.header" class="p-16 sm:p-24">
              <slot name="header" />
            </div>
            <div
              class="border-0 border-t-1 border-border-tertiary"
              :class="{ 'px-16 py-24 sm:px-24 sm:py-32': hasPadding }"
            >
              <slot />
            </div>
          </div>
          <div
            v-if="$slots.footer"
            :class="[
              ` w-full

                ${mediumWidthClass}
                bg-surface-primary
                h-auto


                fixed
                bottom-0
                flex
                justify-evenly

                border-0
                border-t-1
                border-border-tertiary`,
            ]"
          >
            <slot name="footer" />
          </div>
        </div>
      </div>
    </transition>
  </Teleport>
</template>

<script lang="ts" setup>
import { watch } from 'vue';
import { onKeyStroke } from '@vueuse/core';

interface Props {
  dialogTitel: string;
  hasPadding?: boolean;
  drawerExtraClasses?: string;
}

const props = withDefaults(defineProps<Props>(), {
  hasPadding: true,
  drawerExtraClasses: '',
});

const { deactivateFocusTrap, focusTarget } = useModalFocusTrap();
const scrollRef: Ref<HTMLDivElement | null> = ref(null);
const shouldShow = defineModel<boolean>();
const emit = defineEmits(['onClose', 'onScroll']);
const { activatePageScrollLock, deactivatePageScrollLock } =
  usePageScrollLockState(`drawer_${props.dialogTitel}`);

const mediumWidthClass = 'md:w-[44.8rem]';

const close = () => {
  deactivateFocusTrap();
  shouldShow.value = false;
  emit('onClose');
};

onMounted(() => {
  useEventListener(scrollRef, 'scroll', () => {
    emit('onScroll');
  });
});

onUnmounted(() => {
  deactivateFocusTrap();
});

watch(shouldShow, (isOpen) => {
  if (isOpen) {
    activatePageScrollLock();
  } else {
    deactivatePageScrollLock();
    close();
  }
});

onKeyStroke(
  'Escape',
  (e) => {
    close();
  },
  { target: focusTarget },
);
</script>

<style scoped lang="scss">
.slide-enter-active,
.slide-leave-active {
  transition: right 0.3s ease-in-out;
}

.slide-enter-from,
.slide-leave-to {
  right: -30rem;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease-in-out;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* reduced-motion */
@media (prefers-reduced-motion: reduce) {
  .fade-enter-active,
  .fade-leave-active {
    transition: none;
  }

  .slide-enter-active,
  .slide-leave-active {
    transition: none;
  }
}
</style>
